import React, { useCallback, useMemo, useState, useContext } from "react";

import styled from "styled-components";

// datePicker
import { DateObject } from "react-multi-date-picker";
import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";

import { OrderDetailType, OrderItemDetailType } from "../../pages/OrdersPage";

import ColorSchema from "../../assets/kits/colors";
import { ImageSection, OrderLabel } from "./OtherComponents";
import { addCommas } from "@persian-tools/persian-tools";
import CustomButton from "../kits/custom-button/CustomButton";
import { Link, useNavigate } from "react-router-dom";
import useAxios from "../../services/useAxios";
import OrderDrawer from "./OrderDrawer";
import ShoppingCartItemContext from "../../context/ShoppingCart/ShoppingCartItemContext";
import { ReorderResponseType } from "../../types/orders";

const ImageContainer = styled.div`
  width: 100%;
  display: flex;
  overflow-x: auto;
  overflow-y: hidden;
  align-items: center;

  &::-webkit-scrollbar {
    // width: 5px;
    height: 2px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${ColorSchema.gray_300};
    border-radius: 3px;
  }
`;

interface OrderCardProps {
  order: OrderDetailType;
  isFinished?: boolean;
  openInsufficientDrawer?: (theSupplierId: number, theTitles: string[]) => void;
}

const OrderCard: React.FC<OrderCardProps> = ({
  order,
  isFinished,
  openInsufficientDrawer,
}) => {
  const nav = useNavigate();

  const { updateShoppingList } = useContext(ShoppingCartItemContext);

  const { execute: reorderApi, loading: reorder_loading } =
    useAxios("order/reorder");

  const date = useMemo(() => {
    const d = new DateObject({
      date: order.mtm,
      calendar: persian,
      locale: persian_fa,
    });

    return [
      `${d.weekDay.name}، ${d.day} ${d.month.name} ${d.year}`,
      d.format("HH:mm"),
    ];
  }, [order.mtm]);

  const reorderHandler = useCallback(() => {
    reorderApi({ order_id: "" + order.id }).then(
      (data: ReorderResponseType) => {
        if (data.status == 1) {
          updateShoppingList();
          if (data.warnings.length !== 0) {
            openInsufficientDrawer?.(
              order.supplier_id,
              data.warnings.map((v) => v.title)
            );
          } else {
            nav(`/shop/${order.supplier_id}`);
          }
        }
      }
    );
  }, [order, nav, updateShoppingList, openInsufficientDrawer]);

  const showFactorHandler = useCallback(() => {
    setSelectOrder(order.items);
    setDeliverySelectOrderCost(order.delivery_cost);
    setISOpenFactor(true);
  }, []);
  const [isOpenFactor, setISOpenFactor] = useState(false);
  const [selectOrder, setSelectOrder] = useState<OrderItemDetailType[]>([]);
  const [deliverySelectOrderCost, setDeliverySelectOrderCost] = useState<
    string | number
  >("");

  return (
    <div className="order-card pd-t-12">
      <div
        className="mr-x-20"
        style={{ display: "flex", justifyContent: "space-between" }}
      >
        <Link
          to={`/shop/${order.supplier_id}`}
          className="txt-t2-m"
          style={{
            color: ColorSchema.foreground_background_he,
            textDecoration: "none",
          }}
        >
          {order.supplier_title}
        </Link>
        <OrderLabel status={order.status} />
      </div>
      <p className="desc-l mr-r-20 mr-t-6">
        {date[0]}
        <span
          className="mr-x-12"
          style={{ color: ColorSchema.foreground_background_me }}
        >
          |
        </span>
        {date[1]}
      </p>
      <div
        className="mr-r-20"
        style={{ display: "flex", alignItems: "center" }}
      >
        <p
          className="txt-t2-m pd-t-12 pd-b-6"
          style={{
            color: ColorSchema.foreground_background_me,
            minWidth: 100,
          }}
        >
          {addCommas(order.total_amount)} تومان
        </p>
        <ImageContainer className="mr-r-4 pd-t-12 pd-b-6 pd-l-4">
          {order.items.reduce<JSX.Element[]>((total, item, index) => {
            if (index !== 0) {
              total.push(
                <div
                  key={`order-card-separator-${order.id}-${index}`}
                  style={{
                    width: 1,
                    height: 24,
                    borderLeft: `1px solid ${ColorSchema.foreground_background_me}`,
                    opacity: 0.3,
                  }}
                />
              );
            }

            total.push(
              <ImageSection
                key={`order-card-${order.id}-${item.id}-${index}`}
                id={item.id}
                title={item.title}
                image_src={item.image_src}
                item_count={item.item_count}
              />
            );

            return total;
          }, [])}
        </ImageContainer>
      </div>
      {isFinished && (
        <div className="row mr-x-20 pd-t-6 pd-b-12">
          <CustomButton
            className="col mr-l-16"
            size="large"
            buttonStyle="text"
            onClick={showFactorHandler}
          >
            مشاهده فاکتور
          </CustomButton>
          <CustomButton
            className="col"
            size="large"
            state={reorder_loading ? "loading" : "default"}
            onClick={reorderHandler}
            style={{
              height: 50,
            }}
          >
            سفارش مجدد
          </CustomButton>
        </div>
      )}
      <div>
        <OrderDrawer
          isOpen={isOpenFactor}
          setIsOpen={setISOpenFactor}
          items={selectOrder}
          discount={order.total_discount}
          delivery={deliverySelectOrderCost}
        />
      </div>
    </div>
  );
};

export default OrderCard;
